'use client'

import { Button } from '@/components/ui/button'
import {
  useSalaryFilters,
  useSelectedSpecialty,
} from '@/modules/salary/filters/use-salary-filters'
import { FileSearch } from 'lucide-react'
import { formatJobType } from '@/modules/salary/common/utils'
import { professionOptions } from '@/modules/surveys/configs/onboarding/personal-info-config'
import { ShareSalaryV2 } from '../share/share-salary-v2'
import { SalaryFilterControl } from '../constants'
import { useEffect } from 'react'

export function SalaryEmpty({ isAuthenticated }: { isAuthenticated: boolean }) {
  const {
    resetLoadingResults,
    clearSalaryFilters,
    salaryFilterControls,
    filtersCount,
  } = useSalaryFilters({
    isAuthenticated,
  })
  const selectedSpecialty = useSelectedSpecialty({
    specialtySlug: salaryFilterControls?.[SalaryFilterControl.specialty],
  })

  const professionOption = professionOptions.find(
    (p) => p.slug === salaryFilterControls?.[SalaryFilterControl.profession],
  )

  const message = `Sorry, we don’t have any 
  ${[
    formatJobType(salaryFilterControls?.[SalaryFilterControl.jobType]),
    professionOption?.label,
    selectedSpecialty?.fullName,
  ]
    .filter(Boolean)
    .join(' ')} 
  salaries${
    salaryFilterControls?.[SalaryFilterControl.state]?.label
      ? ` in ${salaryFilterControls[SalaryFilterControl.state].label}`
      : ''
  }.`

  useEffect(() => {
    // when the component mounts, set the loadingResults to null.
    // this happens every time the salary filters are updated.
    resetLoadingResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className="bg-background-page rounded-lg flex justify-center py-8 lg:py-16 px-4 mx-4 lg:mx-0 my-6">
      <div className="flex flex-col items-center w-full max-w-[480px] gap-4">
        <div className="bg-blue-50/20 rounded-lg p-2">
          <FileSearch className="stroke-blue-50" />
        </div>
        <p className="text-copy-primary font-medium text-center">{message}</p>
        <p className="text-center text-copy-accent">
          Try adjusting your filters, or share Marit with your friends in
          medicine so they can add an anonymous salary of their own.
        </p>
        <div className="flex gap-4">
          {filtersCount > 0 ? (
            <Button
              onClick={() => {
                clearSalaryFilters()
              }}
              variant="outline"
            >
              Reset filters
            </Button>
          ) : null}
          <ShareSalaryV2 isAuthenticated={isAuthenticated} />
        </div>
      </div>
    </section>
  )
}
